import { type MouseEvent, useEffect, useState } from "react";
import { TriangleAlert } from "lucide-react";

import Loading from "@/components/Loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TraderRepresentativeActionsMenu } from "../TraderRepresentativeActionsMenu";
import { getTraderRepresentativeFullName } from "../traderRepresentativeUtils";
import type {
  RepresentativeActionHandler,
  TraderRepresentative,
  TranslationFunction,
} from "../types";

interface TraderRepresentativesTableProps {
  isLoading?: boolean;
  onClickDeleteRepresentative: RepresentativeActionHandler;
  onClickEditRepresentative: RepresentativeActionHandler;
  representatives?: TraderRepresentative[];
  t: TranslationFunction;
  warningMessage?: string;
}

const REPRESENTATIVES_PAGE_SIZE = 10;

export const TraderRepresentativesTable = ({
  isLoading = false,
  onClickDeleteRepresentative,
  onClickEditRepresentative,
  representatives = [],
  t,
  warningMessage,
}: TraderRepresentativesTableProps) => {
  const [pageIndex, setPageIndex] = useState(0);
  const pageCount = Math.max(1, Math.ceil(representatives.length / REPRESENTATIVES_PAGE_SIZE));
  const pageStart = pageIndex * REPRESENTATIVES_PAGE_SIZE;
  const pageRepresentatives = representatives.slice(
    pageStart,
    pageStart + REPRESENTATIVES_PAGE_SIZE
  );
  const hasRepresentatives = representatives.length > 0;
  const columnCount = 3;

  useEffect(() => {
    setPageIndex(currentPageIndex => Math.min(currentPageIndex, pageCount - 1));
  }, [pageCount]);

  const onClickPage = (event: MouseEvent<HTMLAnchorElement>, nextPageIndex: number) => {
    event.preventDefault();
    setPageIndex(Math.min(Math.max(nextPageIndex, 0), pageCount - 1));
  };

  if (!isLoading && !hasRepresentatives) {
    if (warningMessage) {
      return (
        <Alert variant="warning" className="mb-8">
          <TriangleAlert className="size-4" />
          <AlertDescription>{warningMessage}</AlertDescription>
        </Alert>
      );
    }

    return null;
  }

  return (
    <div className="mb-8 space-y-3">
      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("traderDetails.form.column.name")}</TableHead>
              <TableHead>{t("traderDetails.form.column.email")}</TableHead>
              <TableHead className="w-12 text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <Loading isVisible small />
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading && hasRepresentatives
              ? pageRepresentatives.map((representative, index) => (
                  <TableRow key={representative.id || representative.email || index}>
                    <TableCell className="font-medium text-foreground">
                      {getTraderRepresentativeFullName(representative)}
                    </TableCell>
                    <TableCell>{representative.email || "-"}</TableCell>
                    <TableCell className="text-right">
                      <TraderRepresentativeActionsMenu
                        representative={representative}
                        onClickDeleteRepresentative={onClickDeleteRepresentative}
                        onClickEditRepresentative={onClickEditRepresentative}
                        t={t}
                      />
                    </TableCell>
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </div>
      {pageCount > 1 ? (
        <Pagination className="justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                disabled={pageIndex === 0}
                onClick={event => onClickPage(event, pageIndex - 1)}
              />
            </PaginationItem>
            <PaginationItem>
              <span className="px-3 text-muted-foreground text-sm">
                {pageIndex + 1} / {pageCount}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                disabled={pageIndex >= pageCount - 1}
                onClick={event => onClickPage(event, pageIndex + 1)}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      ) : null}
    </div>
  );
};

export type { TraderRepresentativesTableProps };
